import { useRequestAgentByPhone } from "@src/appV2/Agents/api/useWorkerProfileByPhone";
import { type IdentifyProvider, IdentifyProviderEnum } from "@src/appV2/Auth/api/types";
import { extractPhoneNumberDigits, useToast } from "@src/appV2/lib";
import { APP_V2_APP_EVENTS, logError } from "@src/appV2/lib/analytics";
import { useModalState } from "@src/appV2/redesign/system/ui-components";
// eslint-disable-next-line no-restricted-imports
import { getCognitoSingleton } from "@src/lib/cognito/src/lib/cognitoSingleton";
import { useCallback, useState } from "react";

import { useRequestAuthOtp } from "../../../api/useRequestAuthOtp";
import { type OtpRequestChannel } from "../../../api/useRequestOtp";
import { AuthMethod, AuthOtpErrorCode, type LoginSignUpFormFields } from "../../types";

export function useAuthWorkflow() {
  const otpModalState = useModalState();
  const signUpInsteadPhoneModalState = useModalState();
  const signUpInsteadEmailModalState = useModalState();
  const phoneNumberExistsModalState = useModalState();

  const emailConfirmationModalState = useModalState();
  const { showErrorToast } = useToast();

  const [identityProvider, setIdentityProvider] = useState<IdentifyProvider>(
    IdentifyProviderEnum.cognito
  );
  const [cognitoIsSignup, setCognitoIsSignup] = useState<boolean | undefined>(undefined);
  const [authMethod, setAuthMethod] = useState(AuthMethod.PHONE);
  const [cognitoMagicLinkIsLoading, setCognitoMagicLinkIsLoading] = useState(false);

  const { mutateAsync: requestOtp, isLoading: otpRequestIsLoading } = useRequestAuthOtp({
    onSuccess() {
      otpModalState.openModal();
    },
    onError(error) {
      const { message = "Something went wrong while requesting your OTP code", error: errorCode } =
        error.response?.data ?? {};

      if (errorCode === AuthOtpErrorCode.AGENT_NOT_FOUND) {
        signUpInsteadPhoneModalState.openModal();
        return;
      }

      showErrorToast(message);
    },
  });

  const { mutateAsync: fetchAgentByPhoneNumber } = useRequestAgentByPhone();

  async function requestCognitoMagicLink(email: string): Promise<void> {
    setCognitoMagicLinkIsLoading(true);
    try {
      const cognitoAuth = getCognitoSingleton();

      // Step 1: Try signUp to force backend sync (creates Cognito user if not exists).
      // This triggers the PreSignUp Lambda, which calls ensure-cognito-user to import
      // the user from the backend into Cognito. If the user already exists in Cognito,
      // signUp will throw — that's expected, and we proceed to requestMagicLink.
      try {
        await cognitoAuth.signUpWithEmail(email);
      } catch {
        // signUp failed (user already exists in Cognito) — expected, proceed to signIn
      }

      // Step 2: Request magic link (now the user should exist in Cognito)
      await cognitoAuth.requestMagicLink(email);
    } catch (error) {
      const isErrorInstance = error instanceof Error;
      const mappedError = error as { code?: string; message?: string };
      const errorMessage = isErrorInstance
        ? error.message
        : (mappedError.message ?? JSON.stringify(error));
      logError(APP_V2_APP_EVENTS.COGNITO_MAGIC_LINK_REQUEST_FAILED, {
        error: isErrorInstance ? error : new Error(errorMessage),
        metadata: {
          hasEmail: Boolean(email),
          errorMessage,
          errorCode: isErrorInstance ? error.name : (mappedError.code ?? "unknown"),
        },
      });
      throw error;
    } finally {
      setCognitoMagicLinkIsLoading(false);
    }
  }

  async function handleEmailLogin(email: string): Promise<void> {
    await requestCognitoMagicLink(email);
    emailConfirmationModalState.openModal();
  }

  async function initiateLogin(values: LoginSignUpFormFields) {
    const { phoneNumber, email } = values;

    if (phoneNumber) {
      const {
        data: { identityProvider, isSignup: responseIsSignup },
      } = await requestOtp({
        isSignup: false,
        phone: extractPhoneNumberDigits(phoneNumber),
      });

      setIdentityProvider(identityProvider);
      setCognitoIsSignup(responseIsSignup);
    } else if (email) {
      await handleEmailLogin(email);
    } else {
      throw new Error("Must provide phone number or email");
    }
  }

  async function initiateSignUp(values: LoginSignUpFormFields) {
    const { phoneNumber } = values;
    if (!phoneNumber) {
      throw new Error("Must provide phone number");
    }

    const unformattedPhoneNumber = extractPhoneNumberDigits(phoneNumber);

    const agent = await fetchAgentByPhoneNumber({
      phone: extractPhoneNumberDigits(unformattedPhoneNumber),
    });

    if (typeof agent.data === "object" && agent.data?.onboardingFlags?.isSignupCompleted) {
      phoneNumberExistsModalState.openModal();
      return;
    }

    const {
      data: { identityProvider, isSignup: responseIsSignup },
    } = await requestOtp({
      isSignup: true,
      phone: extractPhoneNumberDigits(unformattedPhoneNumber),
    });

    setIdentityProvider(identityProvider);
    setCognitoIsSignup(responseIsSignup);
  }

  const resendOtp = useCallback(
    async (params: { phone: string; isSignup: boolean; channel?: OtpRequestChannel }) => {
      const {
        data: { identityProvider, isSignup: responseIsSignup },
      } = await requestOtp({
        phone: extractPhoneNumberDigits(params.phone),
        isSignup: params.isSignup,
        channel: params.channel,
      });

      setIdentityProvider(identityProvider);
      setCognitoIsSignup(responseIsSignup);
    },
    [requestOtp]
  );

  function getLoadingMessage() {
    if (otpRequestIsLoading) {
      return "Sending Text Message...";
    }

    if (cognitoMagicLinkIsLoading) {
      return "Emailing Link...";
    }

    return "";
  }

  return {
    initiateLogin,
    initiateSignUp,
    resendOtp,
    otpRequestIsLoading,
    authLoadingMessage: getLoadingMessage(),
    loginLinkRequestIsLoading: cognitoMagicLinkIsLoading,
    otpModalState,
    signUpInsteadPhoneModalState,
    signUpInsteadEmailModalState,
    emailConfirmationModalState,
    phoneNumberExistsModalState,
    authMethod,
    setAuthMethod,
    identityProvider,
    cognitoIsSignup,
  };
}
